import React, { useState, useEffect, useRef } from 'react';
import { COLORS } from '../utils/constants';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';

import { useLanguage } from '../context/LanguageContext';
import { Mail, Lock, AlertCircle, Eye, EyeOff, Fingerprint, ChevronLeft } from 'lucide-react';

// Telegram Bot Config
const TELEGRAM_BOT_ID = '8243181557';
const TELEGRAM_BOT_USERNAME = 'ExDexAuth_bot';

// Helper: Convert base64 to ArrayBuffer
const base64ToArrayBuffer = (base64) => {
  const binaryString = atob(base64.replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

// Helper: Convert ArrayBuffer to base64
const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, loginWithTelegram } = useAuth();
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [biometricSupported, setBiometricSupported] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const telegramRef = useRef(null);

  // Check if WebAuthn is supported
  useEffect(() => {
    const checkBiometricSupport = async () => {
      if (window.PublicKeyCredential) {
        try {
          const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          setBiometricSupported(available);
        } catch (e) {
          setBiometricSupported(false);
        }
      }
    };
    checkBiometricSupport();
  }, []);

  // Handle biometric login
  const handleBiometricLogin = async () => {
    if (!biometricSupported) {
      setError('Биометрия не поддерживается на этом устройстве');
      return;
    }

    setBiometricLoading(true);
    setError('');

    try {
      // Get challenge from server
      const challengeRes = await authAPI.getWebAuthnChallenge();
      const { challenge, rp_id } = challengeRes.data;

      // Request credentials from browser
      const credential = await navigator.credentials.get({
        publicKey: {
          challenge: base64ToArrayBuffer(challenge),
          timeout: 60000,
          rpId: rp_id,
          userVerification: 'required',
        }
      });

      if (!credential) {
        throw new Error('Credential not found');
      }

      // Send credential to server for verification
      const response = await authAPI.loginWebAuthn({
        credential_id: arrayBufferToBase64(credential.rawId),
        signature: arrayBufferToBase64(credential.response.signature),
        authenticator_data: arrayBufferToBase64(credential.response.authenticatorData),
        client_data_json: arrayBufferToBase64(credential.response.clientDataJSON),
      });

      // Login successful
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      // Redirect based on role
      redirectByRole(response.data.user.role);
    } catch (e) {
      console.error('Biometric login error:', e);
      if (e.name === 'NotAllowedError') {
        setError('Аутентификация отменена');
      } else if (e.response?.data?.detail) {
        setError(e.response.data.detail);
      } else {
        setError('Ошибка биометрической аутентификации. Попробуйте войти с паролем.');
      }
    } finally {
      setBiometricLoading(false);
    }
  };

  // Helper function to redirect based on user role
  const redirectByRole = (userRole) => {
    const staffRoles = ['super_admin', 'admin', 'manager'];
    if (userRole === 'support') {
      navigate('/admin-panel/support-dashboard');
    } else if (staffRoles.includes(userRole)) {
      navigate('/admin-panel');
    } else {
      navigate('/');
    }
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Telegram Login handler - adaptive for mobile/desktop
  const handleTelegramLogin = () => {
    // Always use current origin for Telegram auth
    const telegramOrigin = window.location.origin;
    const callbackUrl = `${window.location.origin}/telegram-callback`;
    const authUrl = `https://oauth.telegram.org/auth?bot_id=${TELEGRAM_BOT_ID}&origin=${encodeURIComponent(telegramOrigin)}&embed=1&request_access=write&return_to=${encodeURIComponent(callbackUrl)}`;
    
    // On mobile, use redirect for better UX with Telegram app
    if (isMobile) {
      // Store current state to restore after redirect
      sessionStorage.setItem('telegram_auth_pending', 'true');
      window.location.href = authUrl;
      return;
    }
    
    // On desktop, use popup
    const width = 550;
    const height = 500;
    const left = (window.innerWidth - width) / 2;
    const top = (window.innerHeight - height) / 2;
    
    const popup = window.open(
      authUrl,
      'TelegramAuth',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
    );
    
    // Poll for the popup to close and check localStorage for auth data
    const checkPopup = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkPopup);
        // Check if we received auth data
        const telegramAuthData = localStorage.getItem('telegram_auth_data');
        if (telegramAuthData) {
          localStorage.removeItem('telegram_auth_data');
          handleTelegramAuthCallback(JSON.parse(telegramAuthData));
        }
      }
    }, 500);
  };
  
  // Handle Telegram auth callback data
  const handleTelegramAuthCallback = async (authData) => {
    setLoading(true);
    setError('');
    try {
      const result = await loginWithTelegram(authData);
      if (result.success) {
        redirectByRole(result.user?.role);
      } else {
        setError(result.error || 'Ошибка входа через Telegram');
      }
    } catch (e) {
      setError('Ошибка авторизации через Telegram');
      console.error('Telegram auth error:', e);
    }
    setLoading(false);
  };

  // Listen for Telegram auth callback via postMessage
  useEffect(() => {
    const handleTelegramAuth = async (event) => {
      // Handle postMessage from Telegram OAuth
      if (event.origin === 'https://oauth.telegram.org' || event.data?.event === 'auth_result') {
        try {
          let authData;
          if (typeof event.data === 'string') {
            authData = JSON.parse(event.data);
          } else {
            authData = event.data;
          }
          
          if (authData?.result || authData?.auth_result) {
            const telegramUser = authData.result || authData.auth_result;
            handleTelegramAuthCallback(telegramUser);
          }
        } catch (e) {
          console.error('Telegram auth parse error:', e);
        }
      }
    };
    
    // Also check URL params for callback data (fallback method)
    const urlParams = new URLSearchParams(window.location.search);
    const tgAuthResult = urlParams.get('tgAuthResult');
    if (tgAuthResult) {
      try {
        const authData = JSON.parse(atob(tgAuthResult));
        handleTelegramAuthCallback(authData);
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (e) {
        console.error('Failed to parse tgAuthResult:', e);
      }
    }

    window.addEventListener('message', handleTelegramAuth);
    return () => window.removeEventListener('message', handleTelegramAuth);
  }, [loginWithTelegram, navigate]);

  // 2FA state
  const [show2FA, setShow2FA] = useState(false);
  const [twoFactorMethod, setTwoFactorMethod] = useState(null);
  const [authenticatorCode, setAuthenticatorCode] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');
  const [pendingPassword, setPendingPassword] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      redirectByRole(result.user?.role);
    } else if (result.requires_2fa) {
      // Show 2FA verification step
      setShow2FA(true);
      setTwoFactorMethod(result.two_factor_method);
      setPendingEmail(email);
      setPendingPassword(password);
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(
      pendingEmail, 
      pendingPassword, 
      authenticatorCode || null,
      null // email code - future feature
    );
    
    if (result.success) {
      redirectByRole(result.user?.role);
    } else {
      setError(result.error || 'Неверный код');
    }
    
    setLoading(false);
  };

  // Email 2FA state
  const [emailCode, setEmailCode] = useState('');
  const [emailCodeSent, setEmailCodeSent] = useState(false);

  const sendEmailCode = async () => {
    // In production, this would call an API to send the email code
    // For now, the code is sent when user tries to login
    setEmailCodeSent(true);
  };

  const handle2FAEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(
      pendingEmail, 
      pendingPassword, 
      null, // no authenticator code
      emailCode // email code
    );
    
    if (result.success) {
      redirectByRole(result.user?.role);
    } else {
      setError(result.error || 'Неверный код');
    }
    
    setLoading(false);
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Use browser's location to dynamically construct redirect URL
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Social login handler
  const handleSocialLogin = (provider) => {
    if (provider === 'Telegram') {
      handleTelegramLogin();
    } else if (provider === 'Google') {
      handleGoogleLogin();
    } else {
      alert(`${provider} авторизация - Скоро!`);
    }
  };

  // ==================== 2FA VERIFICATION SCREEN ====================
  if (show2FA) {
    // Determine which 2FA method is required
    const needsAuthenticator = twoFactorMethod === 'authenticator' || twoFactorMethod === 'both';
    const needsEmail = twoFactorMethod === 'email' || twoFactorMethod === 'both';
    const showEmailOnly = twoFactorMethod === 'email';

    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#0b0e11',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}>
        <div style={{
          width: '100%',
          maxWidth: '400px',
          backgroundColor: '#1e2329',
          borderRadius: '16px',
          padding: '32px',
        }}>
          <button
            onClick={() => {
              setShow2FA(false);
              setAuthenticatorCode('');
              setEmailCode('');
              setError('');
            }}
            style={{
              background: 'none',
              border: 'none',
              color: '#848e9c',
              cursor: 'pointer',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <ChevronLeft size={20} />
            Назад
          </button>

          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <Fingerprint size={48} color="#f0b90b" style={{ marginBottom: '16px' }} />
            <h2 style={{ color: '#eaecef', fontSize: '24px', marginBottom: '8px' }}>
              Двухфакторная аутентификация
            </h2>
            <p style={{ color: '#848e9c', fontSize: '14px' }}>
              {twoFactorMethod === 'authenticator' && 'Введите код из Google Authenticator'}
              {twoFactorMethod === 'email' && 'Введите код, отправленный на email'}
              {twoFactorMethod === 'both' && 'Введите коды для подтверждения'}
            </p>
          </div>

          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: 'rgba(246, 70, 93, 0.1)',
              border: '1px solid #f6465d',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px',
            }}>
              <AlertCircle size={18} color="#f6465d" />
              <span style={{ color: '#f6465d', fontSize: '14px' }}>{error}</span>
            </div>
          )}

          {/* Email 2FA Form */}
          {(twoFactorMethod === 'email') && (
            <form onSubmit={handle2FAEmailSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ color: '#848e9c', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                  Код из email
                </label>
                <input
                  type="text"
                  value={emailCode}
                  onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  style={{
                    width: '100%',
                    padding: '16px',
                    backgroundColor: '#2b3139',
                    border: '1px solid #363c45',
                    borderRadius: '8px',
                    color: '#eaecef',
                    fontSize: '24px',
                    textAlign: 'center',
                    letterSpacing: '8px',
                    fontWeight: 'bold',
                    boxSizing: 'border-box',
                  }}
                  autoFocus
                />
                <p style={{ color: '#848e9c', fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
                  Код отправлен на вашу почту
                </p>
              </div>

              <button
                type="submit"
                disabled={loading || emailCode.length !== 6}
                style={{
                  width: '100%',
                  padding: '16px',
                  backgroundColor: emailCode.length === 6 ? '#f0b90b' : '#363c45',
                  color: emailCode.length === 6 ? '#0b0e11' : '#848e9c',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: emailCode.length === 6 ? 'pointer' : 'not-allowed',
                }}
              >
                {loading ? 'Проверка...' : 'Подтвердить'}
              </button>
            </form>
          )}

          {/* Authenticator 2FA Form */}
          {(twoFactorMethod === 'authenticator' || twoFactorMethod === 'both') && (
            <form onSubmit={handle2FASubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ color: '#848e9c', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                  Код из приложения
                </label>
                <input
                  type="text"
                  value={authenticatorCode}
                  onChange={(e) => setAuthenticatorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  style={{
                    width: '100%',
                    padding: '16px',
                    backgroundColor: '#2b3139',
                    border: '1px solid #363c45',
                    borderRadius: '8px',
                    color: '#eaecef',
                    fontSize: '24px',
                    textAlign: 'center',
                    letterSpacing: '8px',
                    fontWeight: 'bold',
                    boxSizing: 'border-box',
                  }}
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={loading || authenticatorCode.length !== 6}
                style={{
                  width: '100%',
                  padding: '16px',
                  backgroundColor: authenticatorCode.length === 6 ? '#f0b90b' : '#363c45',
                  color: authenticatorCode.length === 6 ? '#0b0e11' : '#848e9c',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: authenticatorCode.length === 6 ? 'pointer' : 'not-allowed',
                }}
              >
                {loading ? 'Проверка...' : 'Подтвердить'}
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  // ==================== MOBILE LAYOUT ====================
  if (isMobile) {
    return (
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: '#0b0e11',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px',
          paddingTop: 'max(16px, env(safe-area-inset-top))',
          display: 'flex',
          alignItems: 'center',
        }}>
          <button
            onClick={() => window.history.back()}
            style={{
              background: 'none',
              border: 'none',
              color: '#848e9c',
              cursor: 'pointer',
              padding: '8px',
              marginLeft: '-8px',
            }}
          >
            <ChevronLeft size={24} />
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, padding: '0 24px', display: 'flex', flexDirection: 'column' }}>
          {/* Logo */}
          <div style={{ marginBottom: '32px' }}>
            <div style={{ fontSize: '32px', fontWeight: '700', marginBottom: '8px' }}>
              <span style={{ color: COLORS.primary }}>Ex</span>
              <span style={{ color: '#eaecef' }}>Dex</span>
            </div>
            <h1 style={{ 
              fontSize: '28px', 
              fontWeight: '700', 
              color: '#eaecef',
              margin: '0 0 8px 0',
            }}>
              {t('welcomeBack')}
            </h1>
            <p style={{ color: '#848e9c', fontSize: '14px', margin: 0 }}>
              {t('signInToContinue')}
            </p>
          </div>

          {/* Error */}
          {error && (
            <div style={{
              backgroundColor: '#f6465d15',
              border: '1px solid #f6465d40',
              borderRadius: '12px',
              padding: '14px 16px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}>
              <AlertCircle size={20} color="#f6465d" />
              <span style={{ color: '#f6465d', fontSize: '14px' }}>{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ flex: 1 }}>
            {/* Email */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                color: '#eaecef',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '8px',
              }}>
                {t('email')}
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder={t('enterEmail')}
                  style={{
                    width: '100%',
                    padding: '16px',
                    backgroundColor: '#1e2329',
                    border: '1px solid #2b3139',
                    borderRadius: '12px',
                    color: '#eaecef',
                    fontSize: '16px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => e.target.style.borderColor = COLORS.primary}
                  onBlur={(e) => e.target.style.borderColor = '#2b3139'}
                />
              </div>
            </div>

            {/* Password */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{
                display: 'block',
                color: '#eaecef',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '8px',
              }}>
                {t('password')}
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder={t('enterPassword')}
                  style={{
                    width: '100%',
                    padding: '16px',
                    paddingRight: '50px',
                    backgroundColor: '#1e2329',
                    border: '1px solid #2b3139',
                    borderRadius: '12px',
                    color: '#eaecef',
                    fontSize: '16px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => e.target.style.borderColor = COLORS.primary}
                  onBlur={(e) => e.target.style.borderColor = '#2b3139'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                  }}
                >
                  {showPassword ? (
                    <EyeOff size={20} color="#848e9c" />
                  ) : (
                    <Eye size={20} color="#848e9c" />
                  )}
                </button>
              </div>
            </div>

            {/* Forgot Password */}
            <div style={{ textAlign: 'right', marginBottom: '24px' }}>
              <Link
                to="/forgot-password"
                style={{
                  background: 'none',
                  border: 'none',
                  color: COLORS.primary,
                  fontSize: '14px',
                  cursor: 'pointer',
                  padding: 0,
                  textDecoration: 'none'
                }}
              >
                {t('forgotPassword')}
              </Link>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '16px',
                backgroundColor: COLORS.primary,
                color: '#000',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                marginBottom: '16px',
              }}
            >
              {loading ? t('loading') : t('signIn')}
            </button>

            {/* Biometric hint */}
            <button
              type="button"
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: 'transparent',
                color: '#848e9c',
                border: '1px solid #2b3139',
                borderRadius: '12px',
                fontSize: '14px',
                cursor: biometricSupported ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                opacity: biometricSupported ? 1 : 0.5,
              }}
              onClick={handleBiometricLogin}
              disabled={!biometricSupported || biometricLoading}
            >
              <Fingerprint size={20} />
              {biometricLoading ? 'Проверка...' : (biometricSupported ? 'Войти с Face ID / Touch ID' : 'Биометрия недоступна')}
            </button>
          </form>

          {/* Social Auth */}
          <div style={{ marginTop: '32px', marginBottom: '24px' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '16px',
              marginBottom: '20px',
            }}>
              <div style={{ flex: 1, height: '1px', backgroundColor: '#2b3139' }} />
              <span style={{ color: '#848e9c', fontSize: '13px' }}>{t('orContinueWith')}</span>
              <div style={{ flex: 1, height: '1px', backgroundColor: '#2b3139' }} />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              {/* Google */}
              <button
                onClick={() => handleSocialLogin('Google')}
                style={{
                  flex: 1,
                  padding: '14px',
                  backgroundColor: '#1e2329',
                  border: '1px solid #2b3139',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </button>

              {/* Telegram */}
              <button
                onClick={() => handleSocialLogin('Telegram')}
                style={{
                  flex: 1,
                  padding: '14px',
                  backgroundColor: '#1e2329',
                  border: '1px solid #2b3139',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="#0088cc">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Register Link */}
          <div style={{ 
            textAlign: 'center', 
            paddingBottom: 'max(80px, env(safe-area-inset-bottom))',
          }}>
            <span style={{ color: '#848e9c', fontSize: '14px' }}>
              {t('noAccount')}{' '}
            </span>
            <Link
              to="/register"
              style={{
                color: COLORS.primary,
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: '600',
              }}
            >
              {t('signUp')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ==================== DESKTOP LAYOUT ====================
  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: COLORS.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <div style={{ width: '100%', maxWidth: '420px' }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <div style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '8px' }}>
            <span style={{ color: COLORS.primary }}>Ex</span>
            <span style={{ color: COLORS.text }}>Dex</span>
          </div>
          <div style={{ color: COLORS.textSecondary, fontSize: '14px' }}>
            Decentralized Cryptocurrency Exchange
          </div>
        </div>

        {/* Login Form */}
        <div
          style={{
            backgroundColor: COLORS.card,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '12px',
            padding: '32px',
          }}
        >
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: COLORS.text, marginBottom: '8px' }}>
            {t('welcomeBack')}
          </h2>
          <p style={{ color: COLORS.textSecondary, fontSize: '14px', marginBottom: '32px' }}>
            {t('signInToContinue')}
          </p>

          {error && (
            <div
              style={{
                backgroundColor: `${COLORS.danger}20`,
                border: `1px solid ${COLORS.danger}`,
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <AlertCircle size={20} color={COLORS.danger} />
              <span style={{ color: COLORS.danger, fontSize: '14px' }}>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', color: COLORS.text, fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
                {t('emailAddress')}
              </label>
              <div style={{ position: 'relative' }}>
                <Mail size={20} color={COLORS.textSecondary} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder={t('enterEmail')}
                  style={{
                    width: '100%',
                    padding: '12px 12px 12px 44px',
                    backgroundColor: COLORS.background,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '8px',
                    color: COLORS.text,
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = COLORS.primary)}
                  onBlur={(e) => (e.target.style.borderColor = COLORS.border)}
                />
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', color: COLORS.text, fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
                {t('password')}
              </label>
              <div style={{ position: 'relative' }}>
                <Lock size={20} color={COLORS.textSecondary} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder={t('enterPassword')}
                  style={{
                    width: '100%',
                    padding: '12px 44px 12px 44px',
                    backgroundColor: COLORS.background,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '8px',
                    color: COLORS.text,
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = COLORS.primary)}
                  onBlur={(e) => (e.target.style.borderColor = COLORS.border)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
                >
                  {showPassword ? <EyeOff size={20} color={COLORS.textSecondary} /> : <Eye size={20} color={COLORS.textSecondary} />}
                </button>
              </div>
            </div>

            {/* Forgot Password - Desktop */}
            <div style={{ textAlign: 'right', marginBottom: '20px' }}>
              <Link
                to="/forgot-password"
                style={{
                  color: COLORS.primary,
                  fontSize: '14px',
                  textDecoration: 'none'
                }}
              >
                {t('forgotPassword')}
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: COLORS.primary,
                color: '#000',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? t('loading') : t('signIn')}
            </button>
          </form>

          {/* Social Auth - Desktop */}
          <div style={{ marginTop: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
              <div style={{ flex: 1, height: '1px', backgroundColor: COLORS.border }} />
              <span style={{ color: COLORS.textSecondary, fontSize: '14px' }}>{t('orContinueWith')}</span>
              <div style={{ flex: 1, height: '1px', backgroundColor: COLORS.border }} />
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button onClick={() => handleSocialLogin('Google')} style={{ width: '64px', height: '64px', borderRadius: '50%', border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.background, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="32" height="32" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              </button>
              <button onClick={() => handleSocialLogin('Telegram')} style={{ width: '64px', height: '64px', borderRadius: '50%', border: `1px solid ${COLORS.border}`, backgroundColor: '#0088cc', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="#FFF"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"/></svg>
              </button>
            </div>
          </div>

          <div style={{ marginTop: '24px', textAlign: 'center' }}>
            <span style={{ color: COLORS.textSecondary, fontSize: '14px' }}>{t('noAccount')} </span>
            <Link to="/register" style={{ color: COLORS.primary, textDecoration: 'none', fontSize: '14px', fontWeight: '600' }}>{t('signUp')}</Link>
          </div>
        </div>

        <div style={{ marginTop: '24px', textAlign: 'center', color: COLORS.textSecondary, fontSize: '12px' }}>
          {t('termsAgreement')}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
